import axios from 'axios';
import {
  ASSIGN_QUESTIONS_TO_SECTION_URL,
  get_request_config,
} from '@/utils/system_constants/constant.js';
import {
  assign_dependent_question,
  create_question,
} from '@/services/question_service';
import { extract_error_message } from '@/utils/api_response_helpers';

export function hasInvalidQuestionForms(question_forms) {
  const has_invalid_question_forms = question_forms.some((form) => {
    const has_text = String(form.question_text || '').trim();
    const has_type = String(form.question_type_id || '').trim();
    const is_invalid = !has_text || !has_type;
    return is_invalid;
  });
  return has_invalid_question_forms;
}

function hasValue(value) {
  const is_present = Boolean(String(value || '').trim());
  return is_present;
}

function hasInvalidDependentField(dependent_question) {
  const required_fields = [
    dependent_question?.question_text,
    dependent_question?.question_type_id,
    dependent_question?.trigger_condition,
  ];
  const is_invalid = required_fields.some((value) => !hasValue(value));
  return is_invalid;
}

function hasInvalidDependentTree(dependent_question) {
  if (hasInvalidDependentField(dependent_question)) {
    return true;
  }

  if (!Array.isArray(dependent_question?.dependant_questions)) {
    return false;
  }

  return dependent_question.dependant_questions.some((child_question) =>
    hasInvalidDependentTree(child_question)
  );
}

export function hasInvalidDependentQuestions(question_forms) {
  const has_invalid_dependents = question_forms.some((form) => {
    const should_check_dependents = form?.show_option_inputs && Array.isArray(form.dependant_questions);
    const contains_invalid_dependents = should_check_dependents
      ? form.dependant_questions.some((dependent_question) => {
        const is_invalid = hasInvalidDependentTree(dependent_question);
        return is_invalid;
      })
      : false;
    return contains_invalid_dependents;
  });
  return has_invalid_dependents;
}

export function resolveQuestionSaveContext(ctx) {
  const form_id = Number(ctx.topBar.formId);
  const active_section = ctx.active_question_section;
  const section_id_value = [
    active_section?.section_id,
    active_section?.section?.id,
    active_section?.id,
    null,
  ].find((value) => value != null);
  const section_id = Number(section_id_value);
  const has_context = Boolean(active_section && form_id && section_id);
  const save_context = {
    active_section,
    form_id,
    has_context,
    section_id,
  };
  return save_context;
}

function buildDependentQuestionPayload(dependant_question) {
  const dependent_data = {
    question_text: dependant_question.question_text,
    question_type_id: dependant_question.question_type_id,
    is_mandatory: dependant_question.is_mandatory,
    is_unique: dependant_question.is_unique,
    trigger_condition: dependant_question.trigger_condition,
    multi_select_options: dependant_question.options,
  };
  return dependent_data;
}

async function assignDependentQuestion(question_id, dependant_question) {
  const assign_response = await assign_dependent_question({
    main_question_id: Number(question_id),
    dependent_data: buildDependentQuestionPayload(dependant_question),
  });

  if (assign_response?.status !== 'success') {
    throw new Error(
      assign_response?.message || 'Failed to assign a dependent question.'
    );
  }

  return assign_response;
}

function resolveAssignedQuestionId(assign_response) {
  return Number(
    assign_response?.data?.question_id
    ?? assign_response?.data?.id
    ?? assign_response?.data?.dependent_question_id
    ?? assign_response?.data?.dependent_question?.id
    ?? assign_response?.question_id
    ?? assign_response?.id
    ?? assign_response?.dependent_question_id
    ?? assign_response?.dependent_question?.id
    ?? null
  ) || null;
}

async function saveDependentQuestions(ctx, question_id, dependant_questions) {
  for (const dependant_question of ctx.serialize_dependent_questions(dependant_questions)) {
    await assignSingleDependentQuestion(ctx, question_id, dependant_question);
  }
}

async function assignSingleDependentQuestion(ctx, question_id, dependant_question) {
  try {
    const assign_response = await assignDependentQuestion(question_id, dependant_question);
    const created_dependent_question_id = resolveAssignedQuestionId(assign_response);

    if (!created_dependent_question_id && Array.isArray(dependant_question?.dependant_questions) && dependant_question.dependant_questions.length) {
      ctx.show_error('Dependent question created, but its ID was missing so nested dependents could not be assigned.');
      return;
    }

    if (created_dependent_question_id && Array.isArray(dependant_question?.dependant_questions) && dependant_question.dependant_questions.length) {
      await saveDependentQuestions(ctx, created_dependent_question_id, dependant_question.dependant_questions);
    }
  } catch (error) {
    ctx.show_error(
      extract_error_message(error, 'Failed to assign dependent questions.')
    );
  }
}

function hasDependentQuestions(form) {
  const has_dependants = Boolean(
    form?.show_option_inputs
    && Array.isArray(form.dependant_questions)
    && form.dependant_questions.length
  );
  return has_dependants;
}

function validateCreatedQuestionResponse(parsed_create_response) {
  const question_id = parsed_create_response?.data?.question_id;

  if (!question_id) {
    throw new Error(
      parsed_create_response?.message || 'Failed to create question.'
    );
  }

  return question_id;
}
function createSingleQuestion(ctx, form, section_id) {
  return create_question(
    ctx.build_question_payload(form, section_id)
  )
    .then((parsed_create_response) => {
      const question_id = validateCreatedQuestionResponse(
        parsed_create_response
      );

      return saveQuestionDependants(ctx, form, question_id).then(() => {
        return question_id;
      });
    })
    .catch((error) => {
      throw new Error(
        extract_error_message(error, 'Failed to create question.')
      );
    });
}

async function saveQuestionDependants(ctx, form, question_id) {
  const has_dependants = hasDependentQuestions(form);
  if (has_dependants) {
    await saveDependentQuestions(ctx, question_id, form.dependant_questions);
  }
}

export function createQuestions(ctx, section_id) {
  const created_question_ids = [];

  return Promise.all(
    ctx.question_forms.map((form) =>
      createSingleQuestion(ctx, form, section_id).then((question_id) => {
        created_question_ids.push(question_id);
        return question_id;
      })
    )
  )
    .then(() => created_question_ids)
    .catch((error) => {
      ctx.show_error(
        extract_error_message(error, 'Failed to create questions.')
      );
      return null;
    });
}

export async function assignQuestionsToSection(question_ids, form_id, section_id) {
  const assign_response = await axios.post(
    ASSIGN_QUESTIONS_TO_SECTION_URL,
    { question_ids: question_ids, form_id: form_id, section_id: section_id },
    get_request_config()
  );
  return assign_response;
}

export function resolveEditQuestionValidation(current_edit_question) {
  const question_id = current_edit_question.question_id;
  const question_text = String(current_edit_question.question_text || '').trim();
  const question_type_id = String(current_edit_question.question_type_id || '').trim();
  const validation = {
    has_question_id: Boolean(question_id),
    has_question_text: Boolean(question_text),
    has_question_type_id: Boolean(question_type_id),
  };
  return validation;
}

export function resolveFormOptionValues(option_inputs = []) {
  const normalized_option_inputs = Array.isArray(option_inputs) ? option_inputs : [];
  const option_values = normalized_option_inputs.map((option) => String(option || '').trim()).filter(Boolean);
  return option_values;
}

export function resolveQuestionTypeRecord(question_types, question_type_id) {
  const selected_type = question_types.find((type) => {
    const is_match = String(type.id) === String(question_type_id);
    return is_match;
  }) || null;
  return selected_type;
}

export function resolveQuestionIdentity(question) {
  const question_id = Number(question?.question_id ?? question?.id ?? question?.backend_id ?? null);
  return question_id;
}

export function resolveQuestionRef(section, question_ref) {
  const question = question_ref && typeof question_ref === 'object'
    ? question_ref
    : section?.questions?.[Number(question_ref)];
  return question;
}
